import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import "./stagequizgame.css";
import { selectAdminToken } from "../../admin/sessionSlice";
import { getQuizStages } from "../../admin/functions/quizStages";
import { getDvSourceImageUrl, toCssUrlValue } from "../../functions/themeAssets";
import { sendReport, extractReportIdFromResponse } from "../../functions/sendReport";

const USER_DATA_KEY = "userData";

const STAGE_MEDIA = {
  image_audio: { image: true, audio: true },
  gif: { image: true, audio: false },
  image3: { image: true, audio: false },
  image4: { image: true, audio: false },
  text3: { image: false, audio: false },
  audio4: { image: false, audio: true },
};

const CONFETTI_COLORS = ["#f97316", "#facc15", "#22c55e", "#3b82f6", "#ec4899", "#a855f7"];

const CHEER_MESSAGES = [
  "Congratulations!",
  "Amazing work!",
  "Fantastic job!",
  "You're on fire!",
  "Well played!",
  "Awesome!",
];

function readSessionUserData() {
  try {
    return JSON.parse(sessionStorage.getItem(USER_DATA_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

function formatTime(seconds) {
  const s = Math.max(0, Number(seconds) || 0);
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

function parseTimeToSeconds(value) {
  if (value == null) return 0;
  const parts = String(value).trim().split(":").map((n) => Number(n));
  if (parts.some((n) => Number.isNaN(n))) return 0;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

function toArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      /* not json */
    }
  }
  return [];
}

function parseRules(value) {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
    } catch {
      /* not json */
    }
  }
  return [];
}

function getOptions(question) {
  return [question.option_one, question.option_two, question.option_three, question.option_four]
    .map((o) => (o == null ? "" : String(o)))
    .filter((o) => o.trim() !== "");
}

/** Small DOM/CSS confetti burst (no canvas, no dependency) — bursts upward from one bottom corner and fades. */
function ConfettiBurst({ side }) {
  const pieces = useMemo(
    () =>
      Array.from({ length: 20 }, (_, i) => ({
        id: i,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        offset: Math.random() * 45,
        delay: Math.random() * 0.15,
        duration: 0.9 + Math.random() * 0.6,
        rotate: Math.round(Math.random() * 360),
        drift: Math.round((Math.random() - 0.5) * 160),
      })),
    []
  );
  return (
    <div className={`sqg-confetti sqg-confetti-${side}`} aria-hidden="true">
      {pieces.map((p) => (
        <span
          key={p.id}
          className="sqg-confetti-piece"
          style={{
            [side]: `${p.offset}%`,
            backgroundColor: p.color,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            "--sqg-drift": `${p.drift}px`,
            "--sqg-rotate": `${p.rotate}deg`,
          }}
        />
      ))}
    </div>
  );
}

export default function StageQuizGamePage() {
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { data: themeData } = useSelector((state) => state.theme);
  const storedUserData = useMemo(() => readSessionUserData(), []);

  const userId = storedUserData?.userId || storedUserData?.userid || storedUserData?.id || "";
  const organizationId = storedUserData?.organizationId || "admin";
  const sessionId = storedUserData?.sessionId || "admin";
  const themeName = themeData?.themename ?? themeData?.themeName ?? themeData?.themeId ?? "";
  const rightPoints = Number(themeData?.points ?? 10) || 10;

  const [stages, setStages] = useState([]);
  const [stagesLoaded, setStagesLoaded] = useState(false);
  const [stageId, setStageId] = useState(null);
  const [progressLoaded, setProgressLoaded] = useState(false);
  const [completedStageIds, setCompletedStageIds] = useState([]);
  const [answers, setAnswers] = useState({}); // "stageId:questionId" -> { selected, correct }
  const [answerLog, setAnswerLog] = useState([]);
  const [score, setScore] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [reportId, setReportId] = useState(storedUserData?.reportId || storedUserData?.report_id || null);
  const [gameCompleted, setGameCompleted] = useState(false);
  const gameCompletedRef = useRef(false);

  const [activeStageIdx, setActiveStageIdx] = useState(0);
  const [activeQIdx, setActiveQIdx] = useState(0);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const audioRef = useRef(null);
  const [shakeKey, setShakeKey] = useState(null);
  const [confettiBursts, setConfettiBursts] = useState([]);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [cheerMessage, setCheerMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const rules = useMemo(() => parseRules(themeData?.rules), [themeData]);

  useEffect(() => {
    gameCompletedRef.current = gameCompleted;
  }, [gameCompleted]);

  // Theme-driven background — the <USER> shell deliberately skips its own
  // background on /game so the game view owns its own backdrop.
  const backgroundImage = useMemo(() => {
    if (!themeData) return "";
    const isMobile = window.matchMedia("(max-width: 992px)").matches;
    const bgKey = isMobile
      ? themeData.background_mob || themeData.mobile_view_image
      : themeData.background_desk || themeData.desktop_view_image;
    return bgKey ? getDvSourceImageUrl(bgKey) : "";
  }, [themeData]);

  useEffect(() => {
    if (!backgroundImage) return undefined;
    const root = document.documentElement;
    const body = document.body;
    const bg = `center / cover no-repeat url("${backgroundImage}")`;
    root.style.background = bg;
    body.style.background = bg;
    root.style.minHeight = "100dvh";
    body.style.minHeight = "100dvh";
    return () => {
      root.style.removeProperty("background");
      root.style.removeProperty("min-height");
      body.style.removeProperty("background");
      body.style.removeProperty("min-height");
    };
  }, [backgroundImage]);

  // The rules popup uses the desktop background image (blurred) as its own
  // backdrop, regardless of device, so it reads the same on every screen size.
  const rulesBgImage = useMemo(() => {
    if (!themeData) return "";
    const bgKey = themeData.background_desk || themeData.desktop_view_image;
    return bgKey ? getDvSourceImageUrl(bgKey) : "";
  }, [themeData]);

  const themeVars = useMemo(
    () => ({
      "--sqg-button-color": themeData?.button_color ?? "#667eea",
      "--sqg-button-text-color": themeData?.button_Textcolor ?? themeData?.button_text_color ?? "#ffffff",
      "--sqg-text-color": themeData?.textcolor ?? themeData?.text_color ?? "#1f2937",
      "--sqg-rules-bg-image": toCssUrlValue(rulesBgImage),
    }),
    [themeData, rulesBgImage]
  );

  // Load stage/question content.
  useEffect(() => {
    if (!adminToken || !themeName) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getQuizStages({ currentTheme: themeName, organizationId, sessionId, token: adminToken });
        if (!cancelled) setStages(data.stages || []);
      } catch (e) {
        if (!cancelled) Swal.fire("Error", e.message || "Failed to load the quiz", "error");
      } finally {
        if (!cancelled) setStagesLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adminToken, themeName, organizationId, sessionId]);

  // Create/resume the session row — same theme-agnostic welcomeStage* endpoints
  // used by every other game in this app.
  useEffect(() => {
    if (!adminToken || !userId || stageId !== null || !themeData) return;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/welcomeStageStart`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
          body: JSON.stringify({
            organizationId,
            sessionId,
            userId,
            themeName,
            email: storedUserData?.email || "",
            name: storedUserData?.name || "",
            user_type: storedUserData?.role || storedUserData?.source || "",
            employeeId: storedUserData?.employeeId || storedUserData?.emp_id || storedUserData?.empId || "",
          }),
        });
        const data = await res.json();
        if (!data.success || !data.id) return;
        setStageId(data.id);
        if (data.progress?.reportId && !reportId) setReportId(data.progress.reportId);

        const progress = data.progress;
        if (progress) {
          const isDone =
            String(progress.status || "").toLowerCase() === "completed" || Number(progress.gameover) > 0;
          if (isDone) {
            gameCompletedRef.current = true;
            setGameCompleted(true);
            try {
              sessionStorage.setItem(USER_DATA_KEY, JSON.stringify({ ...storedUserData, gameover: 1 }));
            } catch (_) {}
            Swal.fire("Already Played", "You have already completed this game.", "info").then(() => {
              window.location.href = process.env.REACT_APP_BASE_URL || "/";
            });
            return;
          }
          const savedCompleted = toArray(progress.completedStates).map(Number).filter((n) => Number.isFinite(n));
          setCompletedStageIds(savedCompleted);
          if (progress.total_score != null) setScore(Number(progress.total_score) || 0);
          if (progress.time != null) setElapsed(parseTimeToSeconds(progress.time));
          const savedLog = Array.isArray(progress.answerLog) ? progress.answerLog : [];
          setAnswerLog(savedLog);
          const map = {};
          savedLog.forEach((a) => {
            if (a && a.key) map[a.key] = { selected: a.selected, correct: !!a.isCorrect };
          });
          setAnswers(map);
        }
        setProgressLoaded(true);
      } catch (error) {
        console.error("Failed to initialize stage:", error);
      }
    })();
  }, [adminToken, userId, organizationId, sessionId, stageId, storedUserData, themeName, reportId, themeData]);

  const pushExternalReport = async (currentScore, currentTime) => {
    const role = storedUserData?.role || storedUserData?.source;
    if (role === "demobypass" || role === "DEMO" || !userId) return;
    try {
      const payload = {
        sessionId,
        organizationId,
        userId,
        role,
        token: storedUserData?.token,
        gameId: storedUserData?.gameId,
        name: storedUserData?.name || "",
        points: currentScore,
        time: formatTime(currentTime),
        reportId: reportId || undefined,
      };
      const response = await sendReport(payload);
      const newReportId = extractReportIdFromResponse(response);
      if (newReportId && newReportId !== reportId) {
        setReportId(newReportId);
        if (stageId && adminToken) {
          try {
            await fetch(`${process.env.REACT_APP_BACKEND_URL}/welcomeStageLinkReport`, {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
              body: JSON.stringify({ organizationId, sessionId, userId, stageId, reportId: newReportId }),
            });
          } catch (linkErr) {
            console.error("Failed to link reportId:", linkErr);
          }
        }
        try {
          sessionStorage.setItem(USER_DATA_KEY, JSON.stringify({ ...storedUserData, reportId: newReportId }));
        } catch (_) {}
      }
    } catch (error) {
      console.error("Failed to send external report:", error);
    }
  };

  const saveProgress = async (completedIds, currentScore, currentTime, log) => {
    if (gameCompletedRef.current || !adminToken || !stageId) return;
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/welcomeStageProgress`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          organizationId,
          sessionId,
          userId,
          stageId,
          time: formatTime(currentTime),
          total_score: currentScore,
          points: currentScore,
          completedStates: completedIds,
          answers: log,
        }),
      });
    } catch (error) {
      console.error("Failed to save progress:", error);
    }
  };

  // Timer.
  useEffect(() => {
    if (!progressLoaded || gameCompleted) return undefined;
    const timer = setInterval(() => setElapsed((prev) => prev + 1), 1000);
    return () => clearInterval(timer);
  }, [progressLoaded, gameCompleted]);

  // Periodic autosave.
  useEffect(() => {
    if (!progressLoaded || gameCompleted) return undefined;
    const id = setInterval(() => {
      if (gameCompletedRef.current) return;
      saveProgress(completedStageIds, score, elapsed, answerLog);
    }, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progressLoaded, gameCompleted, completedStageIds, score, elapsed, answerLog]);

  // Save on unmount.
  useEffect(() => {
    return () => {
      if (gameCompletedRef.current) return;
      saveProgress(completedStageIds, score, elapsed, answerLog);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flatReady = stagesLoaded && progressLoaded;
  // Guards the two effects below: if not one single stage has any questions
  // yet (theme not configured, or the admin-template clone hasn't happened),
  // the "skip an empty stage" safety net would otherwise cascade through all
  // 5 stages in a row and immediately call handleGameComplete(), silently
  // marking the session completed and bouncing the player straight to the
  // leaderboard without ever showing a question.
  const anyQuestionsAnywhere = stages.some((s) => (s.questions?.length || 0) > 0);

  // Advance to the first not-yet-completed stage whenever the completed list
  // changes (initial load AND right after a stage is submitted) — this is the
  // one place stage advancement happens.
  useEffect(() => {
    if (!flatReady || gameCompleted || stages.length === 0 || !anyQuestionsAnywhere) return;
    const idx = stages.findIndex((s) => !completedStageIds.includes(s.id));
    if (idx === -1) {
      handleGameComplete();
      return;
    }
    setActiveStageIdx(idx);
    setActiveQIdx(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flatReady, gameCompleted, stages, completedStageIds, anyQuestionsAnywhere]);

  const activeStage = stages[activeStageIdx];
  const activeQuestions = activeStage?.questions || [];
  const activeQuestion = activeQuestions[activeQIdx];
  const activeKey = activeStage && activeQuestion ? `${activeStage.id}:${activeQuestion.id}` : null;
  const activeAnswer = activeKey ? answers[activeKey] : null;
  const media = activeStage ? STAGE_MEDIA[activeStage.stage_type] : null;
  const options = activeQuestion ? getOptions(activeQuestion) : [];
  const allAnsweredInStage =
    activeQuestions.length > 0 && activeQuestions.every((q) => answers[`${activeStage.id}:${q.id}`]);

  // Silently skip a stage the admin hasn't populated yet, instead of soft-locking the
  // player — but only when at least one OTHER stage actually has content; see
  // anyQuestionsAnywhere above for why this doesn't run when nothing is configured yet.
  useEffect(() => {
    if (!flatReady || gameCompleted || !activeStage || !anyQuestionsAnywhere) return;
    if ((activeStage.questions?.length || 0) > 0) return;
    if (completedStageIds.includes(activeStage.id)) return;
    const next = [...completedStageIds, activeStage.id];
    setCompletedStageIds(next);
    saveProgress(next, score, elapsed, answerLog);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flatReady, gameCompleted, activeStage, anyQuestionsAnywhere]);

  // Stop/reset audio whenever the question changes.
  useEffect(() => {
    setAudioPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [activeKey]);

  const toggleAudio = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      el.play();
      setAudioPlaying(true);
    } else {
      el.pause();
      setAudioPlaying(false);
    }
  };

  const triggerConfetti = () => {
    const id = Date.now() + Math.random();
    setConfettiBursts((prev) => [...prev, id]);
    setTimeout(() => setConfettiBursts((prev) => prev.filter((b) => b !== id)), 1300);
  };

  const triggerShake = (key) => {
    setShakeKey(key);
    setTimeout(() => setShakeKey((k) => (k === key ? null : k)), 600);
  };

  const handleSelectOption = (optText) => {
    if (!activeKey || activeAnswer || !activeQuestion) return;
    const isCorrect = optText.trim().toLowerCase() === String(activeQuestion.correct_answer || "").trim().toLowerCase();
    const newScore = isCorrect ? score + rightPoints : score;
    const entry = {
      key: activeKey,
      stageId: activeStage.id,
      questionId: activeQuestion.id,
      selected: optText,
      correct: activeQuestion.correct_answer,
      isCorrect,
    };
    const newLog = [...answerLog, entry];
    const newAnswers = { ...answers, [activeKey]: { selected: optText, correct: isCorrect } };

    setScore(newScore);
    setAnswerLog(newLog);
    setAnswers(newAnswers);
    saveProgress(completedStageIds, newScore, elapsed, newLog);

    if (isCorrect) {
      triggerConfetti();
    } else {
      triggerShake(activeKey);
    }
  };

  const goPrevQuestion = () => {
    if (activeQuestions.length <= 1) return;
    setActiveQIdx((i) => (i - 1 + activeQuestions.length) % activeQuestions.length);
  };
  const goNextQuestion = () => {
    if (activeQuestions.length <= 1) return;
    setActiveQIdx((i) => (i + 1) % activeQuestions.length);
  };

  const handleSubmitStage = async () => {
    if (!allAnsweredInStage || submitting || !activeStage) return;
    setSubmitting(true);
    const newCompleted = [...completedStageIds, activeStage.id];
    setCheerMessage(CHEER_MESSAGES[Math.floor(Math.random() * CHEER_MESSAGES.length)]);
    setShowSuccessPopup(true);
    triggerConfetti();
    await saveProgress(newCompleted, score, elapsed, answerLog);
    await pushExternalReport(score, elapsed);
    setTimeout(() => {
      setShowSuccessPopup(false);
      setSubmitting(false);
      setCompletedStageIds(newCompleted);
    }, 1800);
  };

  const handleGameComplete = async () => {
    if (gameCompleted || gameCompletedRef.current) return;
    gameCompletedRef.current = true;
    setGameCompleted(true);
    await pushExternalReport(score, elapsed);
    if (stageId && adminToken) {
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/welcomeStageComplete`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
          body: JSON.stringify({
            organizationId,
            sessionId,
            userId,
            stageId,
            time: formatTime(elapsed),
            total_score: score,
            points: score,
            completedStates: completedStageIds,
            answers: answerLog,
          }),
        });
      } catch (error) {
        console.error("Failed to complete stage:", error);
      }
    }
    try {
      sessionStorage.setItem(USER_DATA_KEY, JSON.stringify({ ...storedUserData, gameover: 1 }));
    } catch (_) {}
    navigate("/leaderboard", { replace: true });
  };

  if (!stagesLoaded || !progressLoaded || gameCompleted) {
    return (
      <div className="sqg-page" style={themeVars}>
        <div className="sqg-loading">Loading...</div>
      </div>
    );
  }

  if (!activeStage || !activeQuestion) {
    return (
      <div className="sqg-page" style={themeVars}>
        <div className="sqg-loading">This quiz has no questions configured yet.</div>
      </div>
    );
  }

  return (
    <div className="sqg-page" style={themeVars}>
      <div className="sqg-hud">
        <div className="sqg-badge">Time: {formatTime(elapsed)}</div>
        <div className="sqg-hud-right">
          <div className="sqg-badge">Points: {score}</div>
          <button
            type="button"
            className="sqg-rules-btn"
            onClick={() => setShowRules(true)}
            aria-label="Show rules"
            title="Rules"
          >
            ?
          </button>
        </div>
      </div>

      <div className="sqg-title-block">
        {activeStage.heading && <div className="sqg-eyebrow">{activeStage.heading}</div>}
        <h1 className="sqg-title">{activeStage.title}</h1>
      </div>

      <div className="sqg-carousel">
        <button
          type="button"
          className="sqg-arrow sqg-arrow-left"
          onClick={goPrevQuestion}
          disabled={activeQuestions.length <= 1}
          aria-label="Previous question"
        >
          <i className="fa-solid fa-chevron-left"></i>
        </button>

        <div className="sqg-question-card">
          {media?.image && activeQuestion.image && (
            <img className="sqg-media-img" src={getDvSourceImageUrl(activeQuestion.image)} alt="Question" />
          )}

          {media?.audio && activeQuestion.audio && (
            <div className="sqg-audio-row">
              <span className="sqg-waveform sqg-waveform-left" aria-hidden="true">
                {Array.from({ length: 6 }).map((_, i) => (
                  <span key={i} className={`sqg-wave-bar ${audioPlaying ? "is-animating" : ""}`} />
                ))}
              </span>
              <button type="button" className="sqg-audio-btn" onClick={toggleAudio}>
                <i className={audioPlaying ? "fa-solid fa-pause" : "fa-solid fa-play"}></i>
                {audioPlaying ? " Pause" : " Play"}
              </button>
              <span className="sqg-waveform sqg-waveform-right" aria-hidden="true">
                {Array.from({ length: 6 }).map((_, i) => (
                  <span key={i} className={`sqg-wave-bar ${audioPlaying ? "is-animating" : ""}`} />
                ))}
              </span>
              <audio
                ref={audioRef}
                src={getDvSourceImageUrl(activeQuestion.audio)}
                onEnded={() => setAudioPlaying(false)}
              />
            </div>
          )}

          {!media?.image && !media?.audio && activeQuestion.question && (
            <div className="sqg-text-question">{activeQuestion.question}</div>
          )}
        </div>

        <button
          type="button"
          className="sqg-arrow sqg-arrow-right"
          onClick={goNextQuestion}
          disabled={activeQuestions.length <= 1}
          aria-label="Next question"
        >
          <i className="fa-solid fa-chevron-right"></i>
        </button>
      </div>

      <div className="sqg-options">
        {options.map((opt) => {
          const isSelected = activeAnswer?.selected === opt;
          const isCorrectOpt =
            !!activeAnswer && opt.trim().toLowerCase() === String(activeQuestion.correct_answer || "").trim().toLowerCase();
          const isWrongSelected = isSelected && activeAnswer && !activeAnswer.correct;
          return (
            <button
              key={opt}
              type="button"
              disabled={!!activeAnswer}
              onClick={() => handleSelectOption(opt)}
              className={[
                "sqg-option",
                activeAnswer ? "is-locked" : "",
                isCorrectOpt ? "is-correct" : "",
                isWrongSelected ? "is-wrong" : "",
                isWrongSelected && shakeKey === activeKey ? "is-shaking" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {opt}
            </button>
          );
        })}
      </div>

      {activeQuestions.length > 1 && (
        <div className="sqg-dots">
          {activeQuestions.map((q, i) => (
            <button
              type="button"
              key={q.id}
              className={[
                "sqg-dot",
                i === activeQIdx ? "active" : "",
                answers[`${activeStage.id}:${q.id}`] ? "answered" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => setActiveQIdx(i)}
              aria-label={`Go to question ${i + 1}`}
            />
          ))}
        </div>
      )}

      {allAnsweredInStage && (
        <div className="sqg-submit-row">
          <button type="button" className="sqg-submit-btn" disabled={submitting} onClick={handleSubmitStage}>
            {submitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      )}

      {confettiBursts.map((id) => (
        <React.Fragment key={id}>
          <ConfettiBurst side="left" />
          <ConfettiBurst side="right" />
        </React.Fragment>
      ))}

      {showSuccessPopup && (
        <div className="sqg-success-overlay">
          <div className="sqg-success-check">
            <i className="fa-solid fa-trophy"></i>
          </div>
          <div className="sqg-success-text">{cheerMessage || "Congratulations!"}</div>
        </div>
      )}

      {showRules && (
        <div className="sqg-rules-overlay" onClick={() => setShowRules(false)}>
          <div className="sqg-rules-card" onClick={(e) => e.stopPropagation()}>
            <div className="sqg-rules-scroll">
              <div className="sqg-rules-header">
                <h3>How to Play</h3>
                <button
                  type="button"
                  className="sqg-rules-close"
                  onClick={() => setShowRules(false)}
                  aria-label="Close rules"
                >
                  <i className="fa-solid fa-times"></i>
                </button>
              </div>
              {rules.length > 0 ? (
                <ul className="sqg-rules-list">
                  {rules.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              ) : (
                <p className="sqg-rules-empty">No rules have been added for this game yet.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
